/**
 * 促核销组件工具函数
 * 包含数据转换、分类、排序、聚合等核心算法
 */

import {
  AGGREGATION_THRESHOLD,
  CardItemType,
  CategoryType,
  CouponType,
  DisplayItem,
  MyMemberGoodsResp,
  ONE_DAY_MS,
  SEVEN_DAYS_MS,
  TWENTY_FOUR_HOURS_MS,
  UnifiedCardItem,
  UserChargeCardDto,
  UserCouponDto,
} from "../types/coupon-redemption";

// ==================== 工具函数 ====================

/**
 * 格式化金额（分转元）
 * @param amountInCents 金额（分）
 * @returns 格式化后的金额字符串（元）
 */
export function formatAmount(amountInCents: number): string {
  const yuan = amountInCents / 100;
  return Number.isInteger(yuan) ? yuan.toString() : yuan.toFixed(2);
}

// ==================== 数据转换 ====================

/**
 * 将单个优惠券转换为统一数据结构
 *
 * 字段映射：
 * - receiveTime → receiveTime（到账时间，毫秒时间戳）
 * - expireTime  → expireTime（过期时间，毫秒时间戳）
 *
 * @param coupon 优惠券数据
 * @param index 索引（用于生成唯一ID）
 * @returns 统一卡券数据
 */
export function transformCouponToUnifiedItem(
  coupon: UserCouponDto,
  index: number,
): UnifiedCardItem {
  return {
    ...coupon,
    id: `coupon_${coupon.couponPlanId}_${index}`,
    itemType: CardItemType.Coupon,
    receiveTime: coupon.receiveTime || 0,
    expireTime: coupon.expireTime || 0,
  };
}

/**
 * 将单个储值卡转换为统一数据结构
 *
 * 字段映射（卡和券字段名不同，这里统一映射）：
 * - buyTime → receiveTime（到账时间，毫秒时间戳）
 * - invTime → expireTime（过期时间，毫秒时间戳）
 *
 * @param card 储值卡数据
 * @param index 索引（用于生成唯一ID）
 * @returns 统一卡券数据
 */
export function transformStoredCardToUnifiedItem(
  card: UserChargeCardDto,
  index: number,
): UnifiedCardItem {
  return {
    ...card,
    id: `card_${card.chargePqNo}_${index}`,
    itemType: CardItemType.StoredCard,
    receiveTime: card.buyTime || 0,
    expireTime: card.invTime || 0,
  };
}

/**
 * 卡券分类算法
 * 根据到账时间和过期时间判断卡券的分类
 *
 * 分类规则（Requirements 2.2, 2.4, 2.6, 2.8）：
 * - 类型1（新到账）：到账时间距今 ≤ 24小时
 * - 类型2（即将到期）：充电券有效期距当前时间 ≤ 7天，且不属于类型1
 * - 类型3（其他）：不属于类型1或类型2的卡券
 *
 * 优先级规则（Requirements 2.8）：
 * - 同时满足类型1和类型2条件时，优先归类为类型1（新到账优先于即将到期）
 *
 * @param item 统一卡券数据
 * @param currentTime 当前时间戳（毫秒），默认为 Date.now()
 * @returns 分类类型
 */
export function categorizeItem(
  item: UnifiedCardItem,
  currentTime: number = Date.now(),
): CategoryType {
  const receiveTime = item.receiveTime;
  const expireTime = item.expireTime;

  // 新到账优先级最高（Requirements 2.2, 2.8）
  // 到账时间距今 ≤ 24小时
  if (currentTime - receiveTime <= TWENTY_FOUR_HOURS_MS) {
    return CategoryType.NewArrival;
  }

  // 即将到期（券和卡均适用）（Requirements 2.4）
  // 有效期距当前时间 ≤ 7天，且未过期
  if (expireTime - currentTime <= SEVEN_DAYS_MS && expireTime > currentTime) {
    return CategoryType.Expiring;
  }

  // 其他（Requirements 2.6）
  return CategoryType.Other;
}

/**
 * 将API响应转换为统一数据结构
 *
 * 数据处理流程：
 * 1. 转换优惠券列表（后端返回的都是有效券，前端不做状态过滤）
 * 2. 转换储值卡列表（后端返回的都是有效卡，前端不做状态过滤）
 * 3. 将两种类型的数据转换为统一的 UnifiedCardItem 结构
 * 4. 合并为统一数组，便于后续统一进行分类、排序、聚合处理
 *
 * @param resp API响应数据
 * @returns 统一卡券数据数组
 */
export function transformToUnifiedItems(
  resp: MyMemberGoodsResp,
): UnifiedCardItem[] {
  // 1. 转换优惠券列表
  const couponItems: UnifiedCardItem[] = (resp.userCouponList || []).map(
    (coupon, index) => transformCouponToUnifiedItem(coupon, index),
  );

  // 2. 转换储值卡列表
  const cardItems: UnifiedCardItem[] = (resp.userChargeCardList || []).map(
    (card, index) => transformStoredCardToUnifiedItem(card, index),
  );

  // 3. 合并为统一数组，后续统一进行分类、排序、聚合处理
  return [...couponItems, ...cardItems];
}

// ==================== 排序算法 ====================

/**
 * 获取 DisplayItem 的代表项（用于排序比较）
 * @param item 展示项
 * @returns 代表项的统一卡券数据
 */
function getRepresentativeItem(item: DisplayItem): UnifiedCardItem {
  return item.isAggregated ? item.aggregatedData!.representative : item.data!;
}

/**
 * 排序算法
 * 按分类和内部规则对展示项进行排序
 *
 * 排序规则：
 * 1. 分类间排序（Requirements 2.1）：类型1（新到账）> 类型2（即将到期）> 类型3（其他）
 * 2. 类型1内部排序（Requirements 2.3）：券和卡混排，按到账时间倒序（越晚发放排在越前）
 * 3. 类型2内部排序（Requirements 2.5）：优先展示优惠券再展示储值卡，同类型内按有效期从近到远排序
 * 4. 类型3内部排序（Requirements 2.7）：券和卡混排，按到账时间倒序（越晚发放排在越前）
 *
 * @param items 展示项数组
 * @returns 排序后的展示项数组（返回新数组，不修改原数组）
 */
export function sortDisplayItems(items: DisplayItem[]): DisplayItem[] {
  return [...items].sort((a, b) => {
    // 1. 先按分类排序：新到账 > 即将到期 > 其他
    if (a.category !== b.category) {
      return a.category - b.category;
    }

    // 2. 同分类内排序
    const itemA = getRepresentativeItem(a);
    const itemB = getRepresentativeItem(b);

    switch (a.category) {
      case CategoryType.NewArrival:
      case CategoryType.Other:
        // 按到账时间倒序（越晚越前）
        return itemB.receiveTime - itemA.receiveTime;

      case CategoryType.Expiring:
        // 优先展示优惠券，再展示储值卡
        if (itemA.itemType !== itemB.itemType) {
          return itemA.itemType === CardItemType.Coupon ? -1 : 1;
        }
        // 同类型按过期时间正序（越快过期越前）
        return itemA.expireTime - itemB.expireTime;

      default:
        return 0;
    }
  });
}

// ==================== 聚合算法 ====================

/**
 * 创建单个展示项（非聚合）
 * @param item 统一卡券数据
 * @param currentTime 当前时间戳
 * @returns 展示项
 */
export function createSingleDisplayItem(
  item: UnifiedCardItem,
  currentTime: number = Date.now(),
): DisplayItem {
  const category = categorizeItem(item, currentTime);
  return {
    id: item.id,
    isAggregated: false,
    category,
    data: item,
    showNewBadge: category === CategoryType.NewArrival,
    showCountdown: category === CategoryType.Expiring,
  };
}

/**
 * 创建聚合展示项
 * @param items 同一组的卡券列表
 * @param templateId 模版ID
 * @param currentTime 当前时间戳
 * @returns 展示项
 */
export function createAggregatedDisplayItem(
  items: UnifiedCardItem[],
  templateId: string,
  currentTime: number = Date.now(),
): DisplayItem {
  // 选择代表项：按到账时间倒序，取最新的一张
  const sortedItems = [...items].sort((a, b) => b.receiveTime - a.receiveTime);
  const representative = sortedItems[0];
  const category = categorizeItem(representative, currentTime);

  return {
    id: `aggregated_${templateId}_${category}`,
    isAggregated: true,
    category,
    aggregatedData: {
      templateId,
      items,
      count: items.length,
      representative,
    },
    showNewBadge: category === CategoryType.NewArrival,
    showCountdown: category === CategoryType.Expiring,
  };
}

/**
 * 生成聚合分组的 key
 *
 * 聚合规则：
 * - 按「券模版ID + 分类类型」进行分组聚合
 * - 对于「即将到期」分类，还需要按「过期时间」进一步细分
 *
 * @param item 统一卡券数据
 * @param category 分类类型
 * @returns 分组 key
 */
function generateGroupKey(
  item: UnifiedCardItem,
  category: CategoryType,
): string {
  // 类型2中还需要按过期时间聚合
  if (category === CategoryType.Expiring) {
    return `${item.couponPlanId}_${category}_${item.expireTime}`;
  }
  return `${item.couponPlanId}_${category}`;
}

/**
 * 分离优惠券和储值卡
 * @param items 统一卡券数据数组
 * @returns 分离后的优惠券和储值卡
 */
function separateCouponsAndCards(items: UnifiedCardItem[]) {
  return {
    coupons: items.filter((item) => item.itemType === CardItemType.Coupon),
    storedCards: items.filter(
      (item) => item.itemType === CardItemType.StoredCard,
    ),
  };
}

/**
 * 按模版和分类对优惠券进行分组
 * @param coupons 优惠券列表
 * @param currentTime 当前时间戳
 * @returns 分组后的 Map
 */
function groupCouponsByTemplate(
  coupons: UnifiedCardItem[],
  currentTime: number,
): Map<string, UnifiedCardItem[]> {
  const groupMap = new Map<string, UnifiedCardItem[]>();

  coupons.forEach((coupon) => {
    const category = categorizeItem(coupon, currentTime);
    const groupKey = generateGroupKey(coupon, category);

    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
    }
    groupMap.get(groupKey)!.push(coupon);
  });

  return groupMap;
}

/**
 * 将分组后的优惠券转换为展示项
 * @param groupMap 分组后的 Map
 * @param currentTime 当前时间戳
 * @returns 展示项数组
 */
function convertGroupsToDisplayItems(
  groupMap: Map<string, UnifiedCardItem[]>,
  currentTime: number,
): DisplayItem[] {
  return Array.from(groupMap.entries()).map(([key, groupItems]) => {
    if (groupItems.length === 1) {
      return createSingleDisplayItem(groupItems[0], currentTime);
    }
    // 从 key 中提取 templateId（第一个下划线之前的部分）
    const templateId = groupItems[0].couponPlanId || key.split("_")[0];
    return createAggregatedDisplayItem(groupItems, templateId, currentTime);
  });
}

/**
 * 聚合算法
 * 按阈值和模版进行聚合
 *
 * 聚合规则（Requirements 3.1, 3.2, 3.5）：
 * 1. 聚合阈值：当生效中的卡券总数量 ≤ 5 时，所有卡券平铺展示，不进行聚合
 * 2. 当总数量 > 5 时，启用聚合逻辑
 * 3. 储值卡不聚合：无论数量多少，储值卡始终单独展示
 * 4. 优惠券聚合规则：
 *    - 按「券模版ID + 分类类型」进行分组聚合
 *    - 同一券模版的券如果属于不同分类，会分别聚合成不同的组
 *    - 对于「即将到期」分类，还需要按「过期时间」进一步细分
 *
 * @param items 统一卡券数据数组
 * @param currentTime 当前时间戳（毫秒），默认为 Date.now()
 * @returns 展示项数组
 */
export function aggregateItems(
  items: UnifiedCardItem[],
  currentTime: number = Date.now(),
): DisplayItem[] {
  const totalCount = items.length;

  // 卡券数量 <= 5 时不聚合（Requirements 3.1）
  if (totalCount <= AGGREGATION_THRESHOLD) {
    return items.map((item) => createSingleDisplayItem(item, currentTime));
  }

  // 分离优惠券和储值卡
  const { coupons, storedCards } = separateCouponsAndCards(items);

  // 储值卡不聚合，直接转换（Requirements 3.5）
  const storedCardDisplayItems = storedCards.map((card) =>
    createSingleDisplayItem(card, currentTime),
  );

  // 优惠券按券模版ID和分类进行聚合（Requirements 3.2）
  const groupMap = groupCouponsByTemplate(coupons, currentTime);

  // 转换为展示项
  const couponDisplayItems = convertGroupsToDisplayItems(groupMap, currentTime);

  return [...couponDisplayItems, ...storedCardDisplayItems];
}

/**
 * 完整的数据处理流程
 * 将 API 响应转换为最终的展示列表
 *
 * 处理流程：
 * 1. transformToUnifiedItems() - 转换并合并为 UnifiedCardItem[]
 * 2. aggregateItems() - 聚合处理（仅优惠券聚合，储值卡不聚合）
 * 3. sortDisplayItems() - 统一排序
 *
 * @param resp API响应数据
 * @param currentTime 当前时间戳（毫秒）
 * @returns 最终展示列表
 */
export function processCardItems(
  resp: MyMemberGoodsResp,
  currentTime: number = Date.now(),
): DisplayItem[] {
  // 1. 转换为统一数据结构
  const unifiedItems = transformToUnifiedItems(resp);

  // 2. 聚合处理
  const displayItems = aggregateItems(unifiedItems, currentTime);

  // 3. 排序
  return sortDisplayItems(displayItems);
}

// ==================== 展示格式化函数 ====================

/**
 * 获取门槛描述
 *
 * 格式化规则（Requirements 5.4, 5.5）：
 * - 优惠券有门槛（thresholdPrice > 0）：显示"满x元可用"，thresholdPrice 单位是分，需要转换为元
 * - 优惠券无门槛（thresholdPrice = 0 或 undefined）：显示"无门槛可用"
 * - 储值卡：显示储值卡余额描述
 *
 * @param item 统一卡券数据
 * @returns 格式化后的门槛描述字符串
 */
export function getThresholdDesc(item: UnifiedCardItem): string {
  if (item.itemType === CardItemType.Coupon) {
    // 门槛单位是分，需要转换为元
    if (item.thresholdPrice && item.thresholdPrice > 0) {
      return `满${formatAmount(item.thresholdPrice)}元可用`;
    }
    return "无门槛可用";
  }
  // 储值卡显示面额描述
  return `${item.chargeCardTotalAmt ?? 0}元储值卡`;
}

/**
 * 数字补零
 * @param num 数字
 * @returns 补零后的字符串（两位）
 */
function padZero(num: number): string {
  return num.toString().padStart(2, "0");
}

/** 倒计时格式化常量 */
const COUNTDOWN_PREFIX = "距失效";
const COUNTDOWN_SEPARATOR = " : ";
const COUNTDOWN_SPACING = "    "; // 4个空格用于视觉对齐
const DAY_SPACING = "  "; // 2个空格用于天数后的对齐

/**
 * 倒计时格式化算法
 *
 * 格式化规则（Requirements 6.1, 6.2）：
 * - 剩余有效期 > 1天：显示"距失效x天"格式
 * - 剩余有效期 ≤ 1天：显示"距失效hh:mm:ss"格式的实时倒计时
 * - 已过期：显示"距失效00:00:00"，保持角标样式一致
 *
 * @param expireTime 过期时间戳（毫秒）
 * @param currentTime 当前时间戳（毫秒），默认为 Date.now()
 * @returns 格式化后的倒计时字符串
 */
export function formatCountdown(
  expireTime: number,
  currentTime: number = Date.now(),
): string {
  const remainingMs = expireTime - currentTime;

  // 已过期，保持 00:00:00 格式
  if (remainingMs <= 0) {
    return `${COUNTDOWN_PREFIX}${COUNTDOWN_SPACING}00${COUNTDOWN_SEPARATOR}00${COUNTDOWN_SEPARATOR}00`;
  }

  // 剩余 > 1天，显示"距失效x天"
  if (remainingMs > ONE_DAY_MS) {
    const days = Math.floor(remainingMs / ONE_DAY_MS);
    return `${COUNTDOWN_PREFIX}${COUNTDOWN_SPACING}${days}${DAY_SPACING}天`;
  }

  // 剩余 ≤ 1天，显示"距失效hh:mm:ss"
  const hours = Math.floor(remainingMs / (60 * 60 * 1000));
  const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));
  const seconds = Math.floor((remainingMs % (60 * 1000)) / 1000);

  return `${COUNTDOWN_PREFIX}${COUNTDOWN_SPACING}${padZero(hours)}${COUNTDOWN_SEPARATOR}${padZero(minutes)}${COUNTDOWN_SEPARATOR}${padZero(seconds)}`;
}
